import { BehaviorSubject, filter, catchError, Observable, tap, shareReplay } from 'rxjs';
import { Injectable } from '@angular/core';

// Environment
import { environment } from '@environments/environment';

// HandleError
import { handleError } from '@shared-functions/handle-error.function';

// HTTP
import { HttpClient, HttpParams } from '@angular/common/http';

// Interfaces
import { GenericResponse } from '@shared-interfaces/generic-response.interface';
import { Usuario } from '../interfaces/usuario.interface';
import { TokenService } from '@auth/services/token.service';

// RxJS

import { UsuarioxRol } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class UsuariosService {
  private readonly API_SERVER = environment.API_SERVER;
  private usuariosCache$: Observable<Usuario[]> | null = null;
  private usuariosCacheToken: string | null = null;
  private usuariosSimpleCache$: Observable<UsuarioxRol[]> | null = null;

  private readonly currentUserSubject = new BehaviorSubject<Usuario | null>(null);

  loadCurrentUser(): Observable<Usuario> {
    if (!this.currentUserSubject.value) {
      this.getCurrentUser().subscribe((user) => {
        this.currentUserSubject.next(user);
      });
    }
    return this.currentUserSubject.asObservable().pipe(filter((user): user is Usuario => !!user));
  }

  limpiarCurrentUser(): void {
    this.currentUserSubject.next(null);
  }

  getEntidadId(): number | null {
    return this.currentUserSubject.value?.entityId ?? null;
  }

  constructor(private readonly http: HttpClient, private readonly tokenService: TokenService) {
    if (typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener('app-session-changed', () => this.clearUsuariosCache());
    }
  }

  // GETS
  getUsuarios(): Observable<Usuario[]> {
    const currentToken = this.tokenService.getToken();

    // Si cambia el token (cambio de sesion/rol), invalida cache automaticamente.
    if (!this.usuariosCache$ || this.usuariosCacheToken !== currentToken) {
      this.usuariosCacheToken = currentToken;
      this.usuariosCache$ = this.http
        .get<Usuario[]>(`${this.API_SERVER}/users`)
        .pipe(catchError(handleError), shareReplay(1));
    }

    return this.usuariosCache$;
  }

  /** Fuerza una nueva petición HTTP, sin caché */
  getUsuariosRefresh(): Observable<Usuario[]> {
    this.usuariosCacheToken = this.tokenService.getToken();
    this.usuariosCache$ = this.http
      .get<Usuario[]>(`${this.API_SERVER}/users`)
      .pipe(catchError(handleError), shareReplay(1));

    return this.usuariosCache$;
  }

  getUsuariosSimple(): Observable<UsuarioxRol[]> {
    if (!this.usuariosSimpleCache$) {
      this.usuariosSimpleCache$ = this.http
        .get<UsuarioxRol[]>(`${this.API_SERVER}/users/simple`)
        .pipe(catchError(handleError), shareReplay(1));
    }
    return this.usuariosSimpleCache$;
  }

  clearUsuariosCache(): void {
    this.usuariosCache$ = null;
    this.usuariosCacheToken = null;
    this.usuariosSimpleCache$ = null;
  }

  getCurrentUser(): Observable<Usuario> {
    return this.http.get<Usuario>(`${this.API_SERVER}/users/current`).pipe(
      tap((user) => console.log('Payload completo del usuario:', user)), // Debug temporal
      catchError(handleError),
    );
  }

  updateEstado(personaId: number): Observable<GenericResponse> {
    return this.http
      .put<GenericResponse>(`${this.API_SERVER}/users/estado/${personaId}`, {})
      .pipe(catchError(handleError));
  }

  getUsuariosByFilter(filtro: any): Observable<UsuarioxRol[]> {
    const params = new HttpParams().appendAll({
      ...filtro,
    });

    return this.http
      .get<UsuarioxRol[]>(`${this.API_SERVER}/users/filter?${params.toString()}`)
      .pipe(catchError(handleError));
  }

  // POSTS
  createAdminBootstrap(
    form: any,
  ): Observable<{ passwordGen: string; usuario: string; personaId: number }> {
    return this.http
      .post<{ passwordGen: string; usuario: string; personaId: number }>(
        `${this.API_SERVER}/users/bootstrap`,
        form,
      )
      .pipe(catchError(handleError));
  }

  createUsuario(usuarioForm: any): Observable<any> {
    return this.http
      .post<any>(`${this.API_SERVER}/users`, usuarioForm)
      .pipe(catchError(handleError));
  }

  checkEmail(email: string, personaId: number): Observable<number> {
    return this.http
      .post<number>(`${this.API_SERVER}/users/check/email`, {
        email,
        personaId,
      })
      .pipe(catchError(handleError));
  }

  checkNumeroDocumento(
    numeroDocumento: number,
    personaId: number,
    tipoDocumento: string,
    desdeInspectores: string,
  ): Observable<number> {
    console.log(
      'mandando peticion ',
      numeroDocumento,
      ' - ',
      tipoDocumento,
      ' - ',
      desdeInspectores,
      ' - ',
      personaId,
    );

    return this.http
      .post<number>(`${this.API_SERVER}/users/check/numero-documento`, {
        numeroDocumento,
        personaId,
        tipoDocumento,
        desdeInspectores,
      })
      .pipe(catchError(handleError));
  }

  checkUsuario(username: string, personaId: number): Observable<number> {
    return this.http
      .post<number>(`${this.API_SERVER}/users/check/usuario`, {
        username,
        personaId,
      })
      .pipe(catchError(handleError));
  }

  // PUTS
  updateUsuario(personaId: number, usuarioForm: any): Observable<GenericResponse> {
    return this.http
      .put<GenericResponse>(`${this.API_SERVER}/users/${personaId}`, usuarioForm)
      .pipe(catchError(handleError));
  }
  resetPassword(usuario: Usuario): Observable<GenericResponse> {
    return this.http
      .put<GenericResponse>(`${this.API_SERVER}/users/reset-password/${usuario.personaId}`, usuario)
      .pipe(catchError(handleError));
  }

  get currentUser$(): Observable<Usuario | null> {
    return this.currentUserSubject.asObservable();
  }
}
